import { useState } from 'react';
import { Button } from './ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Download, FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

export const ExportButtons = ({ 
    data, 
    filename = 'facturas',
    title = 'Reporte de Facturas',
    subtitle = '',
    columns = [
        { header: 'Nombre', key: 'name' },
        { header: 'Descripción', key: 'description' },
        { header: 'Usuario', key: 'user_name' },
        { header: 'Monto', key: 'amount', format: 'currency' },
        { header: 'Estado', key: 'confirmed', format: 'status' },
        { header: 'Fecha', key: 'created_at', format: 'date' }
    ]
}) => {
    const [exporting, setExporting] = useState(false);

    const formatValue = (value, format) => {
        if (value === null || value === undefined) return '-';
        
        switch (format) {
            case 'currency':
                return `$${parseFloat(value).toFixed(2)}`;
            case 'status':
                return value === true ? 'Confirmado' : 'Pendiente';
            case 'date':
                return new Date(value).toLocaleDateString('es-ES', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                });
            default:
                return value || '-';
        }
    };

    const prepareData = () => {
        return data.map(item => {
            const row = {};
            columns.forEach(col => {
                row[col.header] = formatValue(item[col.key], col.format);
            });
            return row;
        });
    };

    const calculateTotals = () => {
        const confirmed = data.filter(i => i.confirmed === true);
        const pending = data.filter(i => i.confirmed !== true);
        
        return {
            totalConfirmed: confirmed.reduce((sum, i) => sum + (i.amount || 0), 0),
            totalPending: pending.reduce((sum, i) => sum + (i.amount || 0), 0),
            countConfirmed: confirmed.length,
            countPending: pending.length,
            total: data.reduce((sum, i) => sum + (i.amount || 0), 0)
        };
    };

    const exportToExcel = async () => {
        setExporting(true);
        try {
            const exportData = prepareData();
            const totals = calculateTotals();
            
            // Create workbook and worksheet
            const wb = XLSX.utils.book_new();
            
            // Add title rows
            const titleRows = [
                [title],
                [subtitle || `Generado: ${new Date().toLocaleString('es-ES')}`],
                [],
            ];
            
            // Add data
            const ws = XLSX.utils.aoa_to_sheet(titleRows);
            XLSX.utils.sheet_add_json(ws, exportData, { origin: 'A4' });
            
            // Add totals at the end
            const lastRow = exportData.length + 5;
            XLSX.utils.sheet_add_aoa(ws, [
                [],
                ['RESUMEN'],
                ['Confirmadas:', totals.countConfirmed, 'Total:', `$${totals.totalConfirmed.toFixed(2)}`],
                ['Pendientes:', totals.countPending, 'Total:', `$${totals.totalPending.toFixed(2)}`],
                ['TOTAL GENERAL:', data.length, '', `$${totals.total.toFixed(2)}`]
            ], { origin: `A${lastRow}` });
            
            // Set column widths
            ws['!cols'] = columns.map(() => ({ wch: 18 }));
            
            // Style the title
            ws['!merges'] = [
                { s: { r: 0, c: 0 }, e: { r: 0, c: columns.length - 1 } }
            ];
            
            XLSX.utils.book_append_sheet(wb, ws, 'Facturas');
            
            // Generate file
            const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            saveAs(blob, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            alert('Error al exportar a Excel');
        } finally {
            setExporting(false);
        }
    };

    const exportToPDF = async () => {
        setExporting(true);
        try {
            const totals = calculateTotals();
            
            // Create PDF document
            const doc = new jsPDF('landscape', 'mm', 'a4');
            
            // Add title
            doc.setFontSize(18);
            doc.setTextColor(14, 165, 233); // Sky blue
            doc.text(title, 14, 20);
            
            // Add subtitle
            doc.setFontSize(10);
            doc.setTextColor(100);
            doc.text(subtitle || `Generado: ${new Date().toLocaleString('es-ES')}`, 14, 28);
            
            // Prepare table data
            const tableColumns = columns.map(col => col.header);
            const tableRows = data.map(item => 
                columns.map(col => formatValue(item[col.key], col.format))
            );
            
            // Add table using autoTable
            autoTable(doc, {
                head: [tableColumns],
                body: tableRows,
                startY: 35,
                theme: 'striped',
                headStyles: {
                    fillColor: [14, 165, 233], // Sky blue
                    textColor: 255,
                    fontStyle: 'bold'
                },
                alternateRowStyles: {
                    fillColor: [248, 250, 252]
                },
                styles: {
                    fontSize: 9,
                    cellPadding: 3
                },
                columnStyles: {
                    3: { halign: 'right' } // Monto column
                }
            });
            
            // Add totals summary - get finalY from doc
            const finalY = doc.lastAutoTable?.finalY || 150;
            
            doc.setFontSize(11);
            doc.setTextColor(0);
            doc.text('RESUMEN', 14, finalY + 10);
            
            doc.setFontSize(10);
            doc.setTextColor(34, 197, 94); // Green
            doc.text(`Confirmadas: ${totals.countConfirmed} - Total: $${totals.totalConfirmed.toFixed(2)}`, 14, finalY + 17);
            
            doc.setTextColor(245, 158, 11); // Amber
            doc.text(`Pendientes: ${totals.countPending} - Total: $${totals.totalPending.toFixed(2)}`, 14, finalY + 24);
            
            doc.setTextColor(14, 165, 233); // Sky blue
            doc.setFontSize(11);
            doc.text(`TOTAL GENERAL: ${data.length} facturas - $${totals.total.toFixed(2)}`, 14, finalY + 33);
            
            // Add footer
            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFontSize(8);
                doc.setTextColor(150);
                doc.text(
                    `Contabilidad Clara - Página ${i} de ${pageCount}`,
                    doc.internal.pageSize.width / 2,
                    doc.internal.pageSize.height - 10,
                    { align: 'center' }
                );
            }
            
            // Save PDF
            doc.save(`${filename}_${new Date().toISOString().split('T')[0]}.pdf`);
        } catch (error) {
            console.error('Error exporting to PDF:', error);
            alert('Error al exportar a PDF: ' + error.message);
        } finally {
            setExporting(false);
        }
    };

    if (!data || data.length === 0) {
        return null;
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button 
                    variant="outline" 
                    className="border-sky-200 text-sky-600 hover:bg-sky-50"
                    disabled={exporting}
                    data-testid="export-dropdown-btn"
                >
                    {exporting ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                        <Download className="w-4 h-4 mr-2" />
                    )}
                    Exportar
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                <DropdownMenuItem 
                    onClick={exportToExcel}
                    className="cursor-pointer"
                    data-testid="export-excel-btn"
                >
                    <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />
                    Descargar Excel (.xlsx)
                </DropdownMenuItem>
                <DropdownMenuItem 
                    onClick={exportToPDF}
                    className="cursor-pointer"
                    data-testid="export-pdf-btn"
                >
                    <FileText className="w-4 h-4 mr-2 text-red-600" />
                    Descargar PDF
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default ExportButtons;
